// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { zodResolver } from '@hookform/resolvers/zod';
import { useStepsData } from '@shared/ui-kit';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useAccountWorkflowData } from '@shared/advisory/account-workflow/providers';
import {
  accountUsageSchema,
  type AccountUsageFormData,
} from '@shared/advisory/account-workflow/types';
import { useTranslation } from 'react-i18next';
import produce from 'immer';

export const useAccountUsage = () => {
  const { t } = useTranslation();
  const { next } = useStepsData();
  const { getFormData, saveFormData, markStepAsCompleted, isStepCompleted } =
    useAccountWorkflowData();

  const savedData = getFormData('accountUsage');

  const form = useForm<AccountUsageFormData>({
    resolver: zodResolver(accountUsageSchema),
    mode: 'onChange',
    defaultValues: savedData,
  });

  const accountPurposeOptions = useMemo(() => {
    return [
      {
        value: 'savings',
        label: t('account-opening.account-usage.account-purpose.options.savings', {
          ns: 'components',
        }),
      },
      {
        value: 'investment',
        label: t(
          'account-opening.account-usage.account-purpose.options.investment',
          {
            ns: 'components',
          },
        ),
      },
      {
        value: 'transactions',
        label: t(
          'account-opening.account-usage.account-purpose.options.transactions',
          {
            ns: 'components',
          },
        ),
      },
    ];
  }, [t]);

  const monthlyOperationsOptions = useMemo(() => {
    return [
      {
        value: 'ops-tier-low',
        label: t(
          'account-opening.account-usage.monthly-operations.options.ops-tier-low',
          { ns: 'components' },
        ),
      },
      {
        value: 'ops-tier-mid',
        label: t(
          'account-opening.account-usage.monthly-operations.options.ops-tier-mid',
          { ns: 'components' },
        ),
      },
      {
        value: 'ops-tier-high',
        label: t(
          'account-opening.account-usage.monthly-operations.options.ops-tier-high',
          { ns: 'components' },
        ),
      },
    ];
  }, [t]);

  const monthlyTransfersOptions = useMemo(() => {
    return [
      {
        value: 'transfers-tier-low',
        label: t(
          'account-opening.account-usage.monthly-transfers.options.transfers-tier-low',
          { ns: 'components' },
        ),
      },
      {
        value: 'transfers-tier-mid',
        label: t(
          'account-opening.account-usage.monthly-transfers.options.transfers-tier-mid',
          { ns: 'components' },
        ),
      },
      {
        value: 'transfers-tier-high',
        label: t(
          'account-opening.account-usage.monthly-transfers.options.transfers-tier-high',
          { ns: 'components' },
        ),
      },
    ];
  }, [t]);

  const accountChequesOptions = useMemo(() => {
    return [
      {
        value: 'cheques-yes',
        label: t('account-opening.account-usage.account-cheques.options.yes', {
          ns: 'components',
        }),
      },
      {
        value: 'cheques-no',
        label: t('account-opening.account-usage.account-cheques.options.no', {
          ns: 'components',
        }),
      },
    ];
  }, [t]);

  const isFormValid = useCallback(() => {
    return form.formState.isValid;
  }, [form.formState.isValid]);

  const onNext = useCallback(
    async (data: AccountUsageFormData) => {
      if (isStepCompleted('account-usage', form.formState.isDirty)) {
        next();
        return;
      }

      saveFormData('accountUsage', data);

      markStepAsCompleted('account-usage');
      next();
    },
    [
      form.formState.isDirty,
      markStepAsCompleted,
      next,
      saveFormData,
      isStepCompleted,
    ],
  );

  const onToggleAccountPurpose = useCallback(
    (value: string) => {
      const currentValue = form.getValues('accountPurpose');

      const nextValue = produce(currentValue, draft => {
        if (draft.includes(value)) {
          draft.splice(draft.indexOf(value), 1);
        } else {
          draft.push(value);
        }
      });

      form.setValue('accountPurpose', nextValue, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [form],
  );

  return {
    t,
    form,
    onNext,
    isFormValid,
    accountPurposeOptions,
    monthlyOperationsOptions,
    monthlyTransfersOptions,
    accountChequesOptions,
    onToggleAccountPurpose,
  };
};
